import {
  JOB_DEFS,
  DEFAULT_JOB_SCHEDULES,
  type Frequency,
  type JobKind,
  type Schedule,
} from "./audit-data";
import { computeScheduleEvents } from "./schedule-events";
import { createCalendar } from "../ui/calendar";
import { createBadge } from "../ui/badge";
import { fetchHolidays, type Holiday } from "../ui/holiday-api";
import { showToast } from "../ui/toast";
import type { ScheduleDto, SchedulePutBody } from "./schedule-api";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

export interface ScheduleDeps {
  readonly loadHolidays?: (year: number) => Promise<readonly Holiday[]>;
  readonly initialYear?: number;
  readonly initialMonth?: number; // 0-11
  // 実データ配線（接続時）。onSave を渡すと config-check カードが実フォームになり、
  // schedule の現在値で初期化、保存=onSave / 解除=onDelete を呼ぶ。
  // 渡さない場合は従来の全モック（3 ジョブカード + カレンダー）。
  readonly schedule?: ScheduleDto;
  readonly onSave?: (body: SchedulePutBody) => Promise<void>;
  readonly onDelete?: () => Promise<void>;
}

interface MutableSchedule {
  frequency: Frequency;
  dayOfWeek: number;
  dayOfMonth: number;
  time: string;
  notifyTo: readonly string[];
}

function toMutable(s: Schedule): MutableSchedule {
  return { frequency: s.frequency, dayOfWeek: s.dayOfWeek, dayOfMonth: s.dayOfMonth, time: s.time, notifyTo: s.notifyTo };
}

// ジョブ(設定チェック/設定スナップショット/ユーザー棚卸し)ごとにスケジュールを設定し、
// 下部のカレンダーに全ジョブの実行日を tone 色分けで表示する。保存はサンプル(モック)。
export function renderSchedule(root: HTMLElement, deps: ScheduleDeps = {}): void {
  root.replaceChildren();
  const loadHolidays = deps.loadHolidays ?? fetchHolidays;

  const schedules = new Map<JobKind, MutableSchedule>(
    JOB_DEFS.map((j) => [j.kind, toMutable(DEFAULT_JOB_SCHEDULES[j.kind])]),
  );
  let year = deps.initialYear ?? 2026;
  let month = deps.initialMonth ?? 5;
  const holidayCache = new Map<number, readonly Holiday[]>();

  function labeledSelect(
    id: string,
    labelText: string,
    options: ReadonlyArray<{ value: string; label: string }>,
    selected: string,
    onChange: (value: string) => void,
  ): HTMLElement {
    const wrap = document.createElement("div");
    const label = document.createElement("label");
    label.className = "block text-sm font-medium text-slate-700";
    label.htmlFor = id;
    label.textContent = labelText;
    const sel = document.createElement("select");
    sel.id = id;
    sel.className = "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm";
    for (const o of options) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      sel.append(opt);
    }
    sel.value = selected; // 全 option 追加後に現在値を選択（happy-dom で確実に反映）。
    sel.addEventListener("change", () => onChange(sel.value));
    wrap.append(label, sel);
    return wrap;
  }

  function buildJobCard(kind: JobKind, label: string, tone: Parameters<typeof createBadge>[0]["tone"]): HTMLElement {
    const schedule = schedules.get(kind)!;
    const card = document.createElement("section");
    card.dataset["testid"] = "job-card";
    card.className = "rounded-lg border border-slate-200 p-4";

    const heading = document.createElement("h2");
    heading.className = "flex items-center gap-2 text-base font-medium text-slate-900";
    heading.append(createBadge({ label, ...(tone !== undefined ? { tone } : {}) }));
    card.append(heading);

    const form = document.createElement("form");
    form.className = "mt-3 space-y-3";

    const conditional = document.createElement("div");

    function renderConditional(): void {
      conditional.replaceChildren();
      if (schedule.frequency === "weekly") {
        conditional.append(
          labeledSelect(
            "sch-" + kind + "-weekday",
            "曜日",
            WEEKDAYS.map((w, i) => ({ value: String(i), label: w + "曜日" })),
            String(schedule.dayOfWeek),
            (v) => {
              schedule.dayOfWeek = Number(v);
              renderCalendar();
            },
          ),
        );
      } else if (schedule.frequency === "monthly") {
        const days: Array<{ value: string; label: string }> = [];
        for (let d = 1; d <= 28; d += 1) days.push({ value: String(d), label: d + "日" });
        conditional.append(
          labeledSelect("sch-" + kind + "-monthday", "日", days, String(schedule.dayOfMonth), (v) => {
            schedule.dayOfMonth = Number(v);
            renderCalendar();
          }),
        );
      }
    }

    const freq = labeledSelect(
      "sch-" + kind + "-frequency",
      "頻度",
      [
        { value: "off", label: "無効" },
        { value: "weekly", label: "週次" },
        { value: "monthly", label: "月次" },
      ],
      schedule.frequency,
      (v) => {
        schedule.frequency = v as Frequency;
        renderConditional();
        renderCalendar();
      },
    );

    const timeWrap = document.createElement("div");
    const timeLabel = document.createElement("label");
    timeLabel.className = "block text-sm font-medium text-slate-700";
    timeLabel.htmlFor = "sch-" + kind + "-time";
    timeLabel.textContent = "実行時刻";
    const timeInput = document.createElement("input");
    timeInput.id = "sch-" + kind + "-time";
    timeInput.type = "time";
    timeInput.value = schedule.time;
    timeInput.className = "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm";
    timeInput.addEventListener("change", () => {
      schedule.time = timeInput.value;
      renderCalendar();
    });
    timeWrap.append(timeLabel, timeInput);

    const notify = document.createElement("p");
    notify.className = "text-sm text-slate-600";
    notify.textContent = "通知先: " + schedule.notifyTo.join(", ");

    const save = document.createElement("button");
    save.type = "submit";
    save.className = "rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
    save.textContent = "保存";
    const status = document.createElement("p");
    status.setAttribute("role", "status");
    status.className = "text-sm text-slate-700";

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      status.textContent =
        schedule.frequency === "off"
          ? label + " のスケジュールを保存しました（サンプル・自動実行は無効）。"
          : label + " のスケジュールを保存しました（サンプル）。";
      showToast({ tone: "success", title: label + " のスケジュールを保存しました", message: "（サンプル）設定を反映しました。" });
    });

    form.append(freq, conditional, timeWrap, notify, save, status);
    card.append(form);
    renderConditional();
    return card;
  }

  // 実データ配線（接続時）の「設定チェック」カード。現在のスケジュールで初期化し、
  // 保存=onSave / 解除=onDelete を呼ぶ。frequency は weekly|monthly のみ（実フォームは
  // enabled 前提。無効化は「スケジュールを解除」で行う）。
  function buildLiveConfigCheckCard(
    dto: ScheduleDto,
    onSave: (body: SchedulePutBody) => Promise<void>,
    onDelete?: () => Promise<void>,
  ): HTMLElement {
    const def = JOB_DEFS[0]!; // config-check
    // 現在値で初期化（未設定は既定 weekly/月曜/09:00）。
    const state: { frequency: "weekly" | "monthly"; dayOfWeek: number; dayOfMonth: number; time: string } = {
      frequency: dto.frequency ?? "weekly",
      dayOfWeek: dto.dayOfWeek ?? 1,
      dayOfMonth: dto.dayOfMonth ?? 1,
      time: dto.time ?? "09:00",
    };

    const card = document.createElement("section");
    card.dataset["testid"] = "job-card";
    card.className = "rounded-lg border border-slate-200 p-4";

    const heading = document.createElement("h2");
    heading.className = "flex items-center gap-2 text-base font-medium text-slate-900";
    heading.append(createBadge({ label: def.label, tone: def.tone }));
    card.append(heading);

    // 現在の状態表示（enabled / status / 次回目安）。
    const statusLine = document.createElement("p");
    statusLine.dataset["testid"] = "schedule-current-status";
    statusLine.className = "mt-2 text-sm text-slate-600";
    const stateLabel = dto.enabled ? "有効" : "無効";
    const statusText = dto.status !== undefined && dto.status !== "" ? "（" + dto.status + "）" : "";
    statusLine.textContent = "現在の状態: " + stateLabel + statusText;
    card.append(statusLine);

    const form = document.createElement("form");
    form.className = "mt-3 space-y-3";

    const conditional = document.createElement("div");
    function renderConditional(): void {
      conditional.replaceChildren();
      if (state.frequency === "weekly") {
        conditional.append(
          labeledSelect(
            "sch-config-check-weekday",
            "曜日",
            WEEKDAYS.map((w, i) => ({ value: String(i), label: w + "曜日" })),
            String(state.dayOfWeek),
            (v) => {
              state.dayOfWeek = Number(v);
            },
          ),
        );
      } else {
        const days: Array<{ value: string; label: string }> = [];
        for (let d = 1; d <= 28; d += 1) days.push({ value: String(d), label: d + "日" });
        conditional.append(
          labeledSelect("sch-config-check-monthday", "日", days, String(state.dayOfMonth), (v) => {
            state.dayOfMonth = Number(v);
          }),
        );
      }
    }

    const freq = labeledSelect(
      "sch-config-check-frequency",
      "頻度",
      [
        { value: "weekly", label: "週次" },
        { value: "monthly", label: "月次" },
      ],
      state.frequency,
      (v) => {
        state.frequency = v === "monthly" ? "monthly" : "weekly";
        renderConditional();
      },
    );

    const timeWrap = document.createElement("div");
    const timeLabel = document.createElement("label");
    timeLabel.className = "block text-sm font-medium text-slate-700";
    timeLabel.htmlFor = "sch-config-check-time";
    timeLabel.textContent = "実行時刻";
    const timeInput = document.createElement("input");
    timeInput.id = "sch-config-check-time";
    timeInput.type = "time";
    timeInput.value = state.time;
    timeInput.className = "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm";
    timeInput.addEventListener("change", () => {
      state.time = timeInput.value;
    });
    timeWrap.append(timeLabel, timeInput);

    const buttons = document.createElement("div");
    buttons.className = "flex flex-wrap items-center gap-2";

    const save = document.createElement("button");
    save.type = "button";
    save.dataset["testid"] = "schedule-save";
    save.className = "rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
    save.textContent = "保存";
    save.addEventListener("click", () => {
      void onSave({
        frequency: state.frequency,
        dayOfWeek: state.dayOfWeek,
        dayOfMonth: state.dayOfMonth,
        time: state.time,
        // 通知先はバックエンドがテナント管理者から解決するため送信しない（SP3b 決定）。
      });
    });
    buttons.append(save);

    if (dto.enabled && onDelete !== undefined) {
      const disable = document.createElement("button");
      disable.type = "button";
      disable.dataset["testid"] = "schedule-disable";
      disable.className =
        "rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50";
      disable.textContent = "スケジュールを解除";
      disable.addEventListener("click", () => {
        void onDelete();
      });
      buttons.append(disable);
    }

    form.append(freq, conditional, timeWrap, buttons);
    form.addEventListener("submit", (e) => e.preventDefault());
    card.append(form);
    renderConditional();
    return card;
  }

  const liveMode = deps.onSave !== undefined;

  const cards = document.createElement("div");
  cards.className = "grid gap-4 lg:grid-cols-3";
  for (const job of JOB_DEFS) {
    if (liveMode && job.kind === "config-check") {
      cards.append(buildLiveConfigCheckCard(deps.schedule ?? { enabled: false }, deps.onSave!, deps.onDelete));
    } else {
      const card = buildJobCard(job.kind, job.label, job.tone);
      if (liveMode) {
        // 実データ配線は config-check のみ。他 2 ジョブは後続スプリントで対応予定のモック。
        const note = document.createElement("p");
        note.dataset["testid"] = "job-mock-note";
        note.className = "mt-2 text-xs text-slate-500";
        note.textContent =
          job.kind === "snapshot"
            ? "（サンプル）設定スナップショットの定期実行は SP3c で実データ対応予定です。"
            : "（サンプル）ユーザー棚卸しの定期実行は SP3d で実データ対応予定です。";
        card.append(note);
      }
      cards.append(card);
    }
  }

  // ---- カレンダー(全ジョブ統合・tone 色分け) ----
  const calSection = document.createElement("section");
  calSection.className = "mt-8";
  const calHeading = document.createElement("h2");
  calHeading.className = "text-lg font-medium text-slate-900";
  calHeading.textContent = "実行カレンダー";
  const legend = document.createElement("div");
  legend.dataset["testid"] = "calendar-legend";
  legend.className = "mt-2 flex flex-wrap gap-2";
  for (const job of JOB_DEFS) {
    legend.append(createBadge({ label: job.label, tone: job.tone }));
  }
  const calHost = document.createElement("div");
  calHost.className = "mt-3";
  calSection.append(calHeading, legend);
  if (liveMode) {
    // 実データモードでもカレンダーはサンプルの実行日分布を示すモック据置（MVP はカード実フォーム化を主眼）。
    const calNote = document.createElement("p");
    calNote.dataset["testid"] = "calendar-mock-note";
    calNote.className = "mt-2 text-xs text-slate-500";
    calNote.textContent = "（サンプル）実行カレンダーはイメージです。実スケジュールの反映は今後対応します。";
    calSection.append(calNote);
  }
  calSection.append(calHost);

  function renderCalendar(): void {
    const events = JOB_DEFS.flatMap((j) =>
      computeScheduleEvents(schedules.get(j.kind)!, year, month, j.label, j.tone),
    );
    const holidays = holidayCache.get(year) ?? [];
    calHost.replaceChildren(
      createCalendar({
        year,
        month,
        events,
        holidays,
        onNavigate: (y, m) => {
          year = y;
          month = m;
          ensureHolidays(y);
          renderCalendar();
        },
      }),
    );
  }

  function ensureHolidays(y: number): void {
    if (holidayCache.has(y)) return;
    void loadHolidays(y).then((hs) => {
      holidayCache.set(y, hs);
      renderCalendar();
    });
  }

  root.append(cards, calSection);
  ensureHolidays(year);
  renderCalendar();
}
